import { RoomMemberEvent } from 'matrix-js-sdk/src/matrix';
import { useCallback, useEffect, useState } from 'react';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';

import { getJoinedOrInvitedMemberIds } from './getJoinedOrInvitedMemberIds';

const useExistingRoomMemberIds = (excludeRoomId?: string) => {
  const { matrixClient } = useMatrixContext();
  const [memberIds, setMemberIds] = useState<Set<string>>(() => new Set());
  const [isReady, setIsReady] = useState(() => !excludeRoomId);

  const refreshMemberIds = useCallback(() => {
    setMemberIds(getJoinedOrInvitedMemberIds(matrixClient, excludeRoomId));
  }, [matrixClient, excludeRoomId]);

  useEffect(() => {
    if (!excludeRoomId) {
      setMemberIds(new Set());
      setIsReady(true);
      return;
    }

    const room = matrixClient.getRoom(excludeRoomId);
    if (!room) {
      setMemberIds(new Set());
      setIsReady(true);
      return;
    }

    setIsReady(false);

    let cancelled = false;

    room
      .loadMembersIfNeeded()
      .catch(() => undefined)
      .then(() => {
        if (cancelled) return;
        refreshMemberIds();
        setIsReady(true);
      });

    const handleMembershipChange = () => refreshMemberIds();
    matrixClient.on(RoomMemberEvent.Membership, handleMembershipChange);

    return () => {
      cancelled = true;
      matrixClient.off(RoomMemberEvent.Membership, handleMembershipChange);
    };
  }, [matrixClient, excludeRoomId, refreshMemberIds]);

  return { memberIds, isReady };
};

export default useExistingRoomMemberIds;
